/**
 * Canonical report hub taxonomy used by both API and web applications.
 *
 * The report hub exposes generated regulatory artifacts, recurring financial
 * reports, and live analytics surfaces. Web and API must share the same
 * report identifiers, grouping keys, and visibility metadata to avoid drift.
 */
export const CANONICAL_REPORT_EXECUTION_MODES = [
    'GENERATED_ARTIFACT',
    'LIVE_SURFACE',
];
export const CANONICAL_REPORT_IMPLEMENTATION_STATUSES = [
    'IMPLEMENTED',
    'PARTIAL',
    'PLANNED',
];
export const CANONICAL_REPORT_OUTPUT_FORMATS = ['csv'];
export const CANONICAL_REPORT_REQUIRED_JURISDICTIONS = ['NEPAL'];
export const CANONICAL_REPORT_CATEGORIES = [
    'REGULATORY',
    'ACCOUNTING',
    'RISK',
    'PERFORMANCE',
    'OPERATIONS',
    'CLIENT',
    'MARKET',
];
export const CANONICAL_REPORT_GROUPINGS = [
    'CORE_FINANCIAL',
    'IFRS_DISCLOSURE',
    'NEPAL_SEBON',
    'NEPAL_NRB',
    'NEPAL_NEPSE',
    'NEPAL_CDSC',
    'NEPAL_TAX',
    'NEPAL_AUDIT',
    'NEPAL_NFRS',
    'RISK',
    'PERFORMANCE',
    'OPERATIONS',
    'CLIENT',
    'MARKET',
    'FIXED_INCOME',
];
function report(seed) {
    const clientVisible = seed.personas.includes('ORG_CLIENT');
    return {
        id: seed.id,
        titleKey: `reports.catalog.${seed.id}.title`,
        descriptionKey: `reports.catalog.${seed.id}.description`,
        module: seed.module,
        category: seed.category,
        grouping: seed.grouping,
        framework: seed.framework,
        executionMode: seed.executionMode ?? 'GENERATED_ARTIFACT',
        implementationStatus: seed.implementationStatus ?? 'PLANNED',
        clientVisible,
        personas: [...seed.personas],
        scopes: [...seed.scopes],
        parameterKeys: seed.parameterKeys ? [...seed.parameterKeys] : ['asOfDate'],
        outputFormats: seed.outputFormats
            ? [...seed.outputFormats]
            : ['csv'],
        tags: seed.tags ? [...seed.tags] : [],
        defaultParameters: seed.defaultParameters ?? {},
        cadence: seed.cadence,
        requiredJurisdiction: seed.requiredJurisdiction,
    };
}
export const CANONICAL_FINANCIAL_REPORTS = [
    // ─────────────────────────────────────────────────────────────────────────
    // 1. CORE FINANCIAL STATEMENTS (IFRS/GAAP)
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'balance-sheet',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'income-statement',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'cash-flow-statement',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'statement-of-changes-in-equity',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'trial-balance',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'general-ledger',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'accountCode'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'accounts-receivable-aging',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'WEEKLY',
    }),
    report({
        id: 'accounts-payable-aging',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'WEEKLY',
    }),
    report({
        id: 'budget-variance',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'budgetId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'segment-reporting',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'segmentType'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'consolidated-financial-statements',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['asOfDate', 'entityIds'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'interim-financial-report',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'financial-ratios',
        module: 'DATA',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'fund-balance-report',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'multi-currency-consolidation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'baseCurrency'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'intercompany-eliminations',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'fixed-asset-register',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'depreciation-schedule',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'loan-portfolio-summary',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'revenue-recognition',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 2. IFRS DISCLOSURE REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'ifrs9-ecl-impairment',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'stage'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'ifrs13-fair-value-hierarchy',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'hierarchyLevel'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'ifrs16-lease-schedule',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'ifrs15-revenue-disaggregation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'ifrs7-financial-instrument-risk',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'related-party-transactions',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'share-based-payment-valuation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'planId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'impairment-assessment',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'IFRS_DISCLOSURE',
        framework: 'IFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'assetClass'],
        cadence: 'ANNUAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 3. SEBON REPORTS (Securities Board of Nepal)
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'sebon-daily-trading',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-monthly-compliance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-quarterly-financial',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-annual-audit',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-net-capital-adequacy',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-margin-trading-disclosure',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-client-account-segregation',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-insider-trading-surveillance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'WEEKLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-market-manipulation-detection',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'WEEKLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-prospectus-filing',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'securityId'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-mutual-fund-nav',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-portfolio-manager-quarterly',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-foreign-investment-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-short-selling-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-circuit-breaker-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 4. NRB REPORTS (Nepal Rastra Bank)
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'nrb-crr-slr-compliance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'WEEKLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-capital-adequacy-basel',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-large-exposure',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-sectoral-lending',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-stress-test',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'scenarioSetId'],
        cadence: 'SEMI_ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-liquidity-coverage',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-net-stable-funding',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-loan-loss-provisioning',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-npa-aging',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-related-party-lending',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-forex-position',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-interest-rate-risk',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-remittance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-aml-suspicious-transaction',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-ict-risk-assessment',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 5. NEPSE REPORTS (Nepal Stock Exchange)
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'nepse-daily-trading-summary',
        module: 'MARKET_DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-market-depth',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'symbol'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-block-bulk-deals',
        module: 'MARKET_DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-52-week-high-low',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-sector-performance',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-index-composition',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-broker-transaction',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-settlement-obligation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-margin-trading-exposure',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepse-short-sale-disclosure',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NEPSE',
        framework: 'NEPSE',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 6. CDSC REPORTS (Central Depository)
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'cdsc-beneficial-ownership',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_CDSC',
        framework: 'CDSC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'cdsc-demat-remat',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_CDSC',
        framework: 'CDSC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'cdsc-pledge-unpledge',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_CDSC',
        framework: 'CDSC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'cdsc-transfer-transmission',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_CDSC',
        framework: 'CDSC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'cdsc-isin-allocation',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_CDSC',
        framework: 'CDSC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ON_DEMAND',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'cdsc-account-freeze-unfreeze',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_CDSC',
        framework: 'CDSC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ON_DEMAND',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 7. RISK REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'var-report',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'confidenceLevel', 'horizon'],
        cadence: 'DAILY',
    }),
    report({
        id: 'stress-test-results',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'scenarioSetId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'scenario-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'scenarioSetId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'concentration-risk',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'liquidity-risk',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'counterparty-risk',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'credit-risk-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'market-risk-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'operational-risk-events',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'model-validation',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'modelId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'backtesting-results',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'es-report',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'confidenceLevel', 'horizon'],
        cadence: 'DAILY',
    }),
    report({
        id: 'risk-adjusted-return',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'beta-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'benchmarkId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'correlation-matrix',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'drawdown-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'tail-risk-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'risk-limit-utilization',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 8. PERFORMANCE REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'portfolio-performance',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'attribution-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'benchmark-comparison',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'gips-compliance',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        framework: 'GIPS',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'time-weighted-return',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'money-weighted-return',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'alpha-generation',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'sharpe-ratio-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'information-ratio',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'tracking-error',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'sortino-ratio',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'calmar-ratio',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'upside-downside-capture',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'rolling-performance',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'windowDays'],
        cadence: 'DAILY',
    }),
    report({
        id: 'sector-attribution',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId', 'benchmarkId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'security-attribution',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'realized-unrealized-pnl',
        module: 'ACCOUNTING',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'income-report',
        module: 'ACCOUNTING',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'dividend-report',
        module: 'ACCOUNTING',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'fee-report',
        module: 'ACCOUNTING',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 9. OPERATIONS REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'trade-execution-summary',
        module: 'ORDER_MANAGEMENT',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'failed-trade',
        module: 'ORDER_MANAGEMENT',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'settlement-status',
        module: 'ORDER_MANAGEMENT',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'reconciliation-summary',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'data-quality-dashboard',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'audit-trail',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'user-activity',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'system-performance-metrics',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'exception-report',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'workflow-status',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'sla-compliance',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'corporate-action-processing',
        module: 'ORDER_MANAGEMENT',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'cash-projection',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'horizonDays'],
        cadence: 'DAILY',
    }),
    report({
        id: 'collateral-management',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'nav-production-status',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 10. CLIENT-FACING REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'client-portfolio-statement',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'client-holdings',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'client-transaction-history',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'client-performance-summary',
        module: 'DATA',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'client-tax-statement',
        module: 'ACCOUNTING',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'client-capital-gain-loss',
        module: 'ACCOUNTING',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'client-income-statement',
        module: 'ACCOUNTING',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'client-fee-statement',
        module: 'ACCOUNTING',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'client-asset-allocation',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'client-risk-profile',
        module: 'DATA',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'client-investment-policy-compliance',
        module: 'REGULATORY',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['CLIENT', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'client-advisory-notes',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'client-beneficiary-report',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'client-consolidated-view',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 11. MARKET DATA REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'price-history',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd', 'securityId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'volume-analysis',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'securityId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'market-capitalization',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'sector-performance',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'index-performance',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'indexId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'yield-curve-analysis',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'currency'],
        cadence: 'DAILY',
    }),
    report({
        id: 'credit-spread-analysis',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'volatility-surface',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'underlyingId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'economic-indicator',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'indicatorId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'peer-comparison',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'securityId'],
        cadence: 'WEEKLY',
    }),
    report({
        id: 'ipo-pipeline',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'WEEKLY',
    }),
    report({
        id: 'corporate-announcement',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'dividend-calendar',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'earnings-calendar',
        module: 'MARKET_DATA',
        category: 'MARKET',
        grouping: 'MARKET',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION', 'PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 12. FIXED INCOME REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'bond-portfolio-valuation',
        module: 'INVESTMENT',
        category: 'ACCOUNTING',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'duration-convexity-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'yield-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'credit-rating-distribution',
        module: 'DATA',
        category: 'RISK',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'maturity-ladder',
        module: 'INVESTMENT',
        category: 'RISK',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'cash-flow-projection',
        module: 'INVESTMENT',
        category: 'OPERATIONS',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'horizonDays'],
        cadence: 'DAILY',
    }),
    report({
        id: 'coupon-payment-schedule',
        module: 'INVESTMENT',
        category: 'OPERATIONS',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'accrued-interest',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'amortization-schedule',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'FIXED_INCOME',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'securityId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'call-put-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'FIXED_INCOME',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'convertible-bond-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'FIXED_INCOME',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'spread-analysis',
        module: 'DATA',
        category: 'MARKET',
        grouping: 'FIXED_INCOME',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 13. ALTERNATIVE INVESTMENTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'private-equity-valuation',
        module: 'INVESTMENT',
        category: 'ACCOUNTING',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'real-estate-valuation',
        module: 'INVESTMENT',
        category: 'ACCOUNTING',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'hedge-fund-performance',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'commodity-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'cryptocurrency-holdings',
        module: 'INVESTMENT',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'structured-product-valuation',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'securityId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'derivative-portfolio-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'option-greeks',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'swap-valuation',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'forward-contract-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 14. ESG & SUSTAINABILITY
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'esg-scorecard',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'carbon-footprint',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'sustainable-investment-allocation',
        module: 'INVESTMENT',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'controversy-monitoring',
        module: 'REGULATORY',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'impact-measurement',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'tcfd-climate-risk',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'RISK',
        framework: 'TCFD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'sfdr-classification',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'RISK',
        framework: 'SFDR',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'QUARTERLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 15. COMPLIANCE REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'best-execution-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'trade-surveillance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'position-limit-monitoring',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'restricted-list-compliance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'personal-account-dealing',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'code-of-ethics-attestation',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'regulatory-filing-calendar',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'kyc-aml-status',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'sanctions-screening',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'conflict-of-interest',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 16. ANALYTICS & RESEARCH
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'factor-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'style-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'returns-based-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'holdings-based-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'monte-carlo-simulation',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId', 'simulations'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'optimization-results',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'efficient-frontier',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'what-if-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'rebalancing-recommendation',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'tax-loss-harvesting',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 17. MULTI-ASSET & ALLOCATION
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'asset-allocation-current',
        module: 'INVESTMENT',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'asset-allocation-target',
        module: 'INVESTMENT',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'drift-analysis',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'currency-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'geographic-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'sector-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'asset-class-exposure',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'liquidity-tier-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'look-through-analysis',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'overlay-exposure',
        module: 'INVESTMENT',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 18. QUARTERLY & ANNUAL PACKAGES
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'quarterly-investment-review',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'annual-investment-report',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'board-reporting-package',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'regulatory-examination-package',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
    }),
    report({
        id: 'audit-support-package',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'client-onboarding-package',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
    }),
    report({
        id: 'year-end-tax-package',
        module: 'ACCOUNTING',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'due-diligence-package',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 19. ORGANIZATIONAL & BRANCH
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'multi-entity-consolidation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'entity-performance-comparison',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        personas: ['ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'cross-entity-risk-aggregation',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'branch-usage-metrics',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['PLATFORM_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'billing-usage-report',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['PLATFORM_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'license-compliance',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['PLATFORM_ADMIN', 'ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 20. DATA MANAGEMENT
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'data-lineage',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'dataEntityType'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'reference-data-change-log',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'data-completeness',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'vendor-data-quality',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'vendorId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'manual-adjustment-audit',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 21. TECHNOLOGY & SECURITY
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'access-control-audit',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'security-incident-report',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'EVENT_DRIVEN',
    }),
    report({
        id: 'data-breach-notification',
        module: 'ADMIN',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'incidentId'],
        cadence: 'EVENT_DRIVEN',
    }),
    report({
        id: 'system-availability',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'disaster-recovery-test',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'SEMI_ANNUAL',
    }),
    report({
        id: 'vendor-risk-assessment',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'vendorId'],
        cadence: 'ANNUAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 22. INTERNATIONAL STANDARDS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'fatca-crs-reporting',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'FATCA',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'mifid-transaction-reporting',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'MIFID',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'emir-reporting',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'EMIR',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'aifmd-annex-iv',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'AIFMD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'form-pf',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'SEC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'cftc-reporting',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'CFTC',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'basel-iii-pillar3',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'BASEL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'finrep',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'EBA',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'corep',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        framework: 'EBA',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'iso20022-messaging',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        framework: 'ISO',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ON_DEMAND',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 23. INVESTOR RELATIONS
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'fund-factsheet',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'monthly-commentary',
        module: 'DATA',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'shareholder-letter',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'annual-general-meeting-materials',
        module: 'REGULATORY',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'proxy-voting-report',
        module: 'REGULATORY',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN', 'ORG_CLIENT'],
        scopes: ['PORTFOLIO', 'CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'corporate-governance-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 24. CASH & TREASURY
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'cash-flow-forecast',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'horizonDays'],
        cadence: 'DAILY',
    }),
    report({
        id: 'liquidity-forecast',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION', 'PORTFOLIO'],
        parameterKeys: ['asOfDate', 'horizonDays'],
        cadence: 'DAILY',
    }),
    report({
        id: 'working-capital-analysis',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'bank-reconciliation',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'cash-position-by-currency',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'intercompany-loan-tracking',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN'],
        scopes: ['BRANCH'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'treasury-management',
        module: 'ACCOUNTING',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 25. EXPENSE & BUDGET
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'expense-analysis',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'cost-allocation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'profitability-by-client',
        module: 'DATA',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'profitability-by-strategy',
        module: 'DATA',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'vendor-expense-tracking',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'budget-vs-actual',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'budgetId'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 26. OPERATIONAL EFFICIENCY
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'straight-through-processing-rate',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'automation-coverage',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'manual-intervention-tracking',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'error-rate-tracking',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'operational-cost-per-trade',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 27. BUSINESS INTELLIGENCE
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'key-performance-indicators',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'client-acquisition-metrics',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'client-retention-analysis',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'aum-flow-analysis',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'market-share-analysis',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'product-performance-analysis',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 28. CUSTOM & AD HOC
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'ad-hoc-query-results',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'PARTIAL',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'queryId'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'custom-report-builder',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'PARTIAL',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'scheduled-report-status',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'report-distribution-log',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 29. SPECIALIZED ASSET CLASSES
    // ─────────────────────────────────────────────────────────────────────────
    report({
        id: 'fund-of-funds-look-through',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'master-feeder-allocation',
        module: 'INVESTMENT',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'side-pocket-valuation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'series-class-accounting',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'equalization-calculation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'fundId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'performance-fee-calculation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'high-water-mark-tracking',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'hurdle-rate-calculation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'clawback-calculation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'ANNUAL',
    }),
    report({
        id: 'carried-interest-allocation',
        module: 'ACCOUNTING',
        category: 'ACCOUNTING',
        grouping: 'CORE_FINANCIAL',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'ANNUAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 30. REPORTS BY USER ROLE
    // ─────────────────────────────────────────────────────────────────────────
    // 30.1 EXECUTIVE / C-SUITE
    report({
        id: 'executive-dashboard',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'key-metrics-summary',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'exception-alert-summary',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'risk-appetite-dashboard',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'business-performance-overview',
        module: 'DATA',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    // 30.2 PORTFOLIO MANAGER
    report({
        id: 'pm-daily-report',
        module: 'INVESTMENT',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'pm-attribution-report',
        module: 'DATA',
        category: 'PERFORMANCE',
        grouping: 'PERFORMANCE',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'pm-risk-report',
        module: 'DATA',
        category: 'RISK',
        grouping: 'RISK',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    report({
        id: 'pm-trade-idea-tracking',
        module: 'ORDER_MANAGEMENT',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'WEEKLY',
    }),
    report({
        id: 'pm-cash-management',
        module: 'INVESTMENT',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_USER'],
        scopes: ['PORTFOLIO'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'DAILY',
    }),
    // 30.3 COMPLIANCE OFFICER
    report({
        id: 'compliance-monitoring-dashboard',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'breach-notification-log',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'regulatory-change-impact',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'compliance-training-tracking',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'policy-exception-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    // 30.4 OPERATIONS MANAGER
    report({
        id: 'ops-daily-checklist',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'ops-break-report',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'DAILY',
    }),
    report({
        id: 'ops-sla-dashboard',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'ops-team-productivity',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
    }),
    report({
        id: 'ops-vendor-performance',
        module: 'ADMIN',
        category: 'OPERATIONS',
        grouping: 'OPERATIONS',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'vendorId'],
        cadence: 'MONTHLY',
    }),
    // 30.5 CLIENT RELATIONSHIP MANAGER
    report({
        id: 'rm-client-portfolio-overview',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
    }),
    report({
        id: 'rm-client-activity-summary',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        executionMode: 'LIVE_SURFACE',
        implementationStatus: 'IMPLEMENTED',
        personas: ['ORG_ADMIN'],
        scopes: ['CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'WEEKLY',
    }),
    report({
        id: 'rm-client-meeting-notes',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN'],
        scopes: ['CLIENT'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ON_DEMAND',
    }),
    report({
        id: 'rm-client-satisfaction-survey',
        module: 'INVESTMENT',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
    }),
    report({
        id: 'rm-cross-sell-opportunities',
        module: 'DATA',
        category: 'CLIENT',
        grouping: 'CLIENT',
        personas: ['ORG_ADMIN'],
        scopes: ['CLIENT'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 31. ADDITIONAL NEPAL-SPECIFIC REPORTS
    // ─────────────────────────────────────────────────────────────────────────
    // 31.1 SEBON ADDITIONAL
    report({
        id: 'sebon-market-maker-obligation',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-underwriter-quarterly',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-issue-manager-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'issueId'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-dmat-account-verification',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'WEEKLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-online-trading-compliance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-credit-rating-report',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-debenture-trustee-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'debentureId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'sebon-share-registrar-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // 31.2 NRB ADDITIONAL
    report({
        id: 'nrb-mfi-reporting',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-deprived-sector-lending',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-priority-sector-lending',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-agriculture-lending',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-energy-lending',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-tourism-lending',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-wholesale-lending',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-refinancing-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-interest-rate-spread',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nrb-base-rate-calculation',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NRB',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 31.3 NFRS (NEPAL FINANCIAL REPORTING STANDARDS)
    //
    // Nepal converged with IFRS via NFRS, issued by the Institute of Chartered
    // Accountants of Nepal (ICAN) and effective for all Public Interest
    // Entities. The reports below form the NFRS disclosure pack surfaced in
    // the report hub when the Nepal jurisdiction pack is active.
    //
    // NFRS parameter conventions (used by parameterKeys below):
    //   - asOfDate: point-in-time measurement or disclosure date (REPORT_AS_OF_DATE)
    //   - periodStart / periodEnd: flow and movement reporting period boundaries
    //   - portfolioId: optional portfolio scoping for per-portfolio NFRS views
    //   - hierarchyLevel: NFRS 13 fair value hierarchy filter (LEVEL_1 | LEVEL_2 | LEVEL_3)
    //   - eclStage: NFRS 9 impairment stage filter (STAGE_1 | STAGE_2 | STAGE_3)
    //   - valuationTechnique: NFRS 13 valuation technique filter
    //     (MARKET_APPROACH | COST_APPROACH | INCOME_APPROACH)
    //
    // These parameter keys are contract-level metadata: API resolves the
    // parameter forms and web renders them from descriptors, so extending a
    // parameterKeys list is safe for both sides as long as the keys remain
    // self-describing strings.
    // ─────────────────────────────────────────────────────────────────────────
    // NFRS 1 — First-time Adoption of Nepal Financial Reporting Standards
    report({
        id: 'nepal-nfrs-first-time-adoption',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'transitionDate'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 7 — Financial Instruments: Disclosures
    report({
        id: 'nepal-nfrs-financial-instrument-disclosure',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — Financial Instruments (classification, measurement, impairment)
    report({
        id: 'nepal-nfrs9-classification-measurement',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-nfrs9-ecl-impairment',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'eclStage', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-nfrs9-hedge-accounting',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 13 — Fair Value Measurement
    report({
        id: 'nepal-nfrs13-fair-value-hierarchy',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'hierarchyLevel', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-nfrs13-valuation-technique',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'valuationTechnique', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 15 — Revenue from Contracts with Customers
    report({
        id: 'nepal-nfrs15-revenue-recognition',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 16 — Leases
    report({
        id: 'nepal-nfrs16-lease',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 17 — Insurance Contracts
    report({
        id: 'nepal-nfrs17-insurance-contract',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 1 — Presentation of Financial Statements (NFRS-aligned)
    report({
        id: 'nepal-nfrs-presentation-financial-statements',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 7 — Statement of Cash Flows (NFRS-aligned)
    report({
        id: 'nepal-nfrs-cash-flow',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 8 — Accounting Policies, Changes in Accounting Estimates and Errors
    report({
        id: 'nepal-nfrs-accounting-policies',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 10 — Events after the Reporting Period
    report({
        id: 'nepal-nfrs-events-after-reporting',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 12 — Income Taxes
    report({
        id: 'nepal-nfrs-income-taxes',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 16 — Property, Plant and Equipment
    report({
        id: 'nepal-nfrs-property-plant-equipment',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 19 — Employee Benefits
    report({
        id: 'nepal-nfrs-employee-benefits',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 21 — The Effects of Changes in Foreign Exchange Rates
    report({
        id: 'nepal-nfrs-foreign-exchange',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 23 — Borrowing Costs
    report({
        id: 'nepal-nfrs-borrowing-costs',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 24 — Related Party Disclosures
    report({
        id: 'nepal-nfrs-related-party',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 33 — Earnings per Share
    report({
        id: 'nepal-nfrs-earnings-per-share',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 34 — Interim Financial Reporting
    report({
        id: 'nepal-nfrs-interim-reporting',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 36 — Impairment of Assets
    report({
        id: 'nepal-nfrs-impairment-assets',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 37 — Provisions, Contingent Liabilities and Contingent Assets
    report({
        id: 'nepal-nfrs-provisions-contingencies',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 38 — Intangible Assets
    report({
        id: 'nepal-nfrs-intangible-assets',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 40 — Investment Property
    report({
        id: 'nepal-nfrs-investment-property',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 31.4 NFRS 9 DEPTH — CLASSIFICATION & IMPAIRMENT DETAIL
    //
    // Drill-down disclosure reports for NFRS 9 expected credit loss (ECL) and
    // classification workflows. These complement the summary-level NFRS 9
    // reports above with staging, movement, and test-level detail.
    // ─────────────────────────────────────────────────────────────────────────
    // NFRS 9 — ECL staging analysis (Stage 1/2/3 distribution and criteria)
    report({
        id: 'nepal-nfrs9-ecl-staging-analysis',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'eclStage', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — ECL provision matrix (PD/LGD/EAD grid by segment and stage)
    report({
        id: 'nepal-nfrs9-ecl-provision-matrix',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — ECL movement reconciliation (opening to closing by stage)
    report({
        id: 'nepal-nfrs9-ecl-movement-reconciliation',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'eclStage'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — SPPI test assessment (solely payments of principal and interest)
    report({
        id: 'nepal-nfrs9-sppi-test-assessment',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — Business model assessment (hold-to-collect / hold-and-sell / other)
    report({
        id: 'nepal-nfrs9-business-model-assessment',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — Impairment reversal tracking (subsequent recoveries)
    report({
        id: 'nepal-nfrs9-impairment-reversal',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — Modification gain/loss on financial assets
    report({
        id: 'nepal-nfrs9-modification-gain-loss',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 31.5 NFRS 13 DEPTH — FAIR VALUE HIERARCHY DETAIL
    //
    // Drill-down disclosure reports for NFRS 13 fair value measurement:
    // level transfers, Level 3 rollforwards, and unobservable input
    // sensitivity analysis.
    // ─────────────────────────────────────────────────────────────────────────
    // NFRS 13 — Transfers between fair value hierarchy levels
    report({
        id: 'nepal-nfrs13-level-transfers',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'hierarchyLevel'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 13 — Level 3 rollforward (opening to closing reconciliation)
    report({
        id: 'nepal-nfrs13-level3-rollforward',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 13 — Unobservable input sensitivity analysis (Level 3)
    report({
        id: 'nepal-nfrs13-unobservable-input-sensitivity',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'valuationTechnique'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9/13 — Day-one gain/loss deferral and amortization
    report({
        id: 'nepal-nfrs-day-one-gain-loss-deferral',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — Fair value option designation and changes
    report({
        id: 'nepal-nfrs-fair-value-option-designation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 9 — Embedded derivative identification and separation
    report({
        id: 'nepal-nfrs-embedded-derivative-identification',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'portfolioId'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 31.6 NFRS 7 & PRESENTATION DEPTH — DISCLOSURE PACK DETAIL
    //
    // Additional NFRS 7 disclosure-pack components and presentation-level
    // reports that regulators and auditors expect alongside the primary
    // NFRS statements.
    // ─────────────────────────────────────────────────────────────────────────
    // NFRS 7 — Maturity analysis of financial liabilities (contractual flows)
    report({
        id: 'nepal-nfrs7-maturity-analysis',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 7 — Credit concentration risk disclosure
    report({
        id: 'nepal-nfrs7-credit-concentration',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 7 — Collateral and other credit enhancements held
    report({
        id: 'nepal-nfrs7-collateral-credit-enhancement',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 7 — Transferred financial assets not derecognized
    report({
        id: 'nepal-nfrs7-transferred-assets-not-derecognized',
        module: 'DATA',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 24 — Related party disclosure pack (balances, terms, commitments)
    report({
        id: 'nepal-nfrs-related-party-disclosure',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // NFRS 1 — First-time adoption reconciliation (previous GAAP to NFRS)
    report({
        id: 'nepal-nfrs-first-time-adoption-reconciliation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'transitionDate'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    // NAS 8 — Prior period restatement disclosure (errors and policy changes)
    report({
        id: 'nepal-nfrs-prior-period-restatement',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_NFRS',
        framework: 'NFRS',
        personas: ['ORG_ADMIN', 'ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 32. NEPAL TAX (IRD)
    // ─────────────────────────────────────────────────────────────────────────
    // 32.1 INCOME TAX
    report({
        id: 'nepal-advance-tax-calculation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-withholding-tax-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-capital-gains-tax-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd', 'portfolioId'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-dividend-tax-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-tax-return-preparation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fiscalYear'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // 32.2 VAT & OTHER TAXES
    report({
        id: 'nepal-vat-return',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-excise-duty-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-customs-duty-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-tds-certificate-tracking',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // 32.3 NEPAL FISCAL YEAR (BS)
    report({
        id: 'nepal-fiscal-year-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['fiscalYearBS'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-quarterly-advance-tax',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fiscalYearBS'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-annual-tax-audit',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'IRD',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['fiscalYearBS'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 33. NEPAL AUDIT & COMPLIANCE
    // ─────────────────────────────────────────────────────────────────────────
    // 33.1 STATUTORY AUDIT
    report({
        id: 'nepal-statutory-audit-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'ICAN',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['fiscalYearBS'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-internal-audit-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'ICAN',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-compliance-audit',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'ICAN',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-tax-audit',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'ICAN',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['fiscalYearBS'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // 33.2 COMPANY REGISTRAR (OCR)
    report({
        id: 'nepal-annual-return-filing',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'OCR',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-share-register-update',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'OCR',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-board-resolution-filing',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'OCR',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-memorandum-amendment-filing',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'OCR',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    // 33.3 NEPAL AML/CFT
    report({
        id: 'nepal-aml-ctr-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-aml-str-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'EVENT_DRIVEN',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-pep-screening-report',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-sanctions-compliance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_AUDIT',
        framework: 'NRB',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'QUARTERLY',
        requiredJurisdiction: 'NEPAL',
    }),
    // ─────────────────────────────────────────────────────────────────────────
    // 34. NEPAL INVESTMENT VEHICLES
    // ─────────────────────────────────────────────────────────────────────────
    // 34.1 PENSION & PROVIDENT FUNDS
    report({
        id: 'nepal-epf-contribution-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'EPF',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-cit-contribution-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'CIT',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-social-security-fund-report',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'SSF',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['periodStart', 'periodEnd'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-gratuity-fund-valuation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_TAX',
        framework: 'ICAN',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
    // 34.2 NEPAL MUTUAL FUNDS
    report({
        id: 'nepal-mutual-fund-nav-calculation',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'DAILY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-mutual-fund-unit-holder-report',
        module: 'INVESTMENT',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-mutual-fund-investment-compliance',
        module: 'REGULATORY',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'MONTHLY',
        requiredJurisdiction: 'NEPAL',
    }),
    report({
        id: 'nepal-mutual-fund-distribution',
        module: 'ACCOUNTING',
        category: 'REGULATORY',
        grouping: 'NEPAL_SEBON',
        framework: 'SEBON',
        personas: ['ORG_ADMIN', 'ORG_ADMIN'],
        scopes: ['ORGANIZATION'],
        parameterKeys: ['asOfDate', 'fundId'],
        cadence: 'ANNUAL',
        requiredJurisdiction: 'NEPAL',
    }),
];
const CANONICAL_FINANCIAL_REPORT_INDEX = new Map(CANONICAL_FINANCIAL_REPORTS.map((definition) => [definition.id, definition]));
export function getCanonicalFinancialReport(id) {
    return CANONICAL_FINANCIAL_REPORT_INDEX.get(id);
}
export function requireCanonicalFinancialReport(id) {
    const definition = CANONICAL_FINANCIAL_REPORT_INDEX.get(id);
    if (!definition) {
        throw new Error(`Unknown canonical financial report: ${id}`);
    }
    return definition;
}
export function listCanonicalFinancialReportsForPersona(persona) {
    return CANONICAL_FINANCIAL_REPORTS.filter((definition) => definition.personas.includes(persona));
}
export function validateCanonicalFinancialReportTaxonomy() {
    const errors = [];
    const seenIds = new Set();
    for (const definition of CANONICAL_FINANCIAL_REPORTS) {
        if (seenIds.has(definition.id)) {
            errors.push(`Duplicate report id: ${definition.id}`);
        }
        seenIds.add(definition.id);
        if (definition.outputFormats.length === 0) {
            errors.push(`Report ${definition.id} must declare at least one output format.`);
        }
        if (definition.clientVisible !== definition.personas.includes('ORG_CLIENT')) {
            errors.push(`Report ${definition.id} has inconsistent clientVisible flag.`);
        }
        if (definition.clientVisible &&
            !definition.scopes.some((scope) => ['CLIENT', 'PORTFOLIO', 'ORGANIZATION', 'BRANCH'].includes(scope))) {
            errors.push(`Client-visible report ${definition.id} must include a client-compatible scope.`);
        }
        if (definition.requiredJurisdiction &&
            !CANONICAL_REPORT_REQUIRED_JURISDICTIONS.includes(definition.requiredJurisdiction)) {
            errors.push(`Report ${definition.id} declares unknown jurisdiction: ${definition.requiredJurisdiction}`);
        }
    }
    return errors;
}
